/**
 * Every message sent about one claim (WPS-175 requirement 3, first slice).
 *
 * Penny's ask is a Letter card: pick a template, pick a contact, generate,
 * send, and keep a copy on the claim. The "keep a copy so users can open and
 * review them at any time during the claim process" half already exists - the
 * platform logs every send against its record, and the per-record read surface
 * (`records/<type>/<uuid>/activity/`) was built for exactly this timeline but
 * never rendered anywhere. This card renders it.
 *
 * Composing from the template library lands here next, as an action in this
 * header. Deliberately not a "coming soon" stub: a claims agent asking "did the
 * homeowner get the checklist?" is answered by this card today.
 *
 * Tenant-agnostic. Every label, status and channel comes from the backend row;
 * nothing here knows which templates or channels a tenant uses.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClipboardList,
  Eye,
  FileText,
  Loader2,
  Lock,
  Mail,
  Plus,
  Upload,
} from "lucide-react";

import {
  ActivityChannelPill,
  ActivitySkipReason,
  ActivityStatusPill,
} from "@/admin/components/communications/activity-pills";
import { ActivityDetailPanel } from "@/admin/components/communications/activity-detail-panel";
import { ClaimLetterDialog } from "@/admin/components/claims/claim-letter-dialog";
import { RecordForms } from "@/admin/components/forms/record-forms";
import { SendFormDialog } from "@/admin/components/forms/send-form-dialog";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { useFormTemplates } from "@/admin/hooks/use-forms";
import {
  useRecordCommunicationsActivity,
  type ActivityRow,
} from "@/admin/hooks/use-communications-activity";
import { usePermissions } from "@/admin/hooks/use-permissions";
import type { ComposeKind } from "@/admin/pages/claims/use-send-packet";
import { useFormatters } from "@/shared/lib/formatting";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";

/** Enough to answer "what has this claim sent" without an endless scroll. */
const PAGE_SIZE = 12;

/** What "Create New" can send. Each kind opens the composer on its templates. */
const COMPOSE_OPTIONS: {
  kind: ComposeKind;
  icon: typeof Mail;
  labelKey: string;
  label: string;
  hintKey: string;
  hint: string;
}[] = [
  {
    kind: "letter",
    icon: FileText,
    labelKey: "claimComms.create.letter",
    label: "Letter",
    hintKey: "claimComms.create.letterHint",
    hint: "A formal letter, kept on the claim as a PDF",
  },
  {
    kind: "evidence_request",
    icon: Upload,
    labelKey: "claimComms.create.request",
    label: "Document request",
    hintKey: "claimComms.create.requestHint",
    hint: "Ask for documents or photos, with an upload link",
  },
  {
    kind: "notification",
    icon: Mail,
    labelKey: "claimComms.create.email",
    label: "Email",
    hintKey: "claimComms.create.emailHint",
    hint: "Send one of your email templates",
  },
];

export function ClaimCommunicationsCard({
  claimUuid,
  folded,
  onToggleFold,
}: {
  claimUuid: string;
  folded?: boolean;
  onToggleFold?: () => void;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const [openRow, setOpenRow] = useState<ActivityRow | null>(null);
  const [composeKind, setComposeKind] = useState<ComposeKind | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);
  const { can } = usePermissions();
  // Offered only once the org has built a form that can go out from a claim.
  const { data: formTemplates = [] } = useFormTemplates({
    recordType: "claim",
    activeOnly: true,
  });

  const { data, isPending, isError, refetch } = useRecordCommunicationsActivity(
    "claim",
    claimUuid,
    { page_size: PAGE_SIZE },
  );
  const allRows = data?.results ?? [];

  // WPS-146: "claims communications need to have internal and external
  // communications". The same split the Notes card carries, and read for the
  // same reason - "what has the homeowner been told?" and "who inside the
  // business has been pulled in?" are two different questions, and one
  // undifferentiated list answers neither.
  //
  // The audience is the backend's call (membership, not email domain - a
  // tenant's own staff file claims as customers, so the same address lands on
  // both sides). Nothing here re-derives it.
  const [audienceFilter, setAudienceFilter] = useState<
    "all" | "internal" | "external"
  >("all");
  const internalCount = allRows.filter((r) => r.audience === "internal").length;
  const externalCount = allRows.length - internalCount;
  const rows =
    audienceFilter === "all"
      ? allRows
      : allRows.filter((r) => r.audience === audienceFilter);

  return (
    <DetailCard
      title={t("claimComms.title", "Communications")}
      icon={<Mail className="h-3.5 w-3.5" />}
      collapsible={false}
      folded={folded}
      onToggleFold={onToggleFold}
      trailing={
        can("claims.send_packet") ? (
          // The card sends more than letters, so "Create New" names each kind.
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger
              render={
                <Button
                  variant="ghost"
                  size="xs"
                  data-testid="claim-comms-create"
                  className="h-auto px-0 text-[11px] text-[var(--green)]"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t("claimComms.newLetter", "Create New")}
                </Button>
              }
            />
            <PopoverContent side="bottom" align="end" className="w-[280px] p-1.5">
              {COMPOSE_OPTIONS.map((option) => (
                <Button
                  key={option.kind}
                  variant="ghost"
                  data-testid={`claim-comms-create-${option.kind}`}
                  onClick={() => {
                    setMenuOpen(false);
                    setComposeKind(option.kind);
                  }}
                  className="flex h-auto w-full items-start justify-start gap-2.5 rounded-[6px] px-2.5 py-2 text-left font-light"
                >
                  <option.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] text-[var(--ink)]">
                      {t(option.labelKey, option.label)}
                    </span>
                    <span className="whitespace-normal text-[11px] text-[var(--ink-3)]">
                      {t(option.hintKey, option.hint)}
                    </span>
                  </span>
                </Button>
              ))}
              {formTemplates.length > 0 ? (
                <Button
                  variant="ghost"
                  data-testid="claim-comms-create-form"
                  onClick={() => {
                    setMenuOpen(false);
                    setSendingForm(true);
                  }}
                  className="flex h-auto w-full items-start justify-start gap-2.5 rounded-[6px] px-2.5 py-2 text-left font-light"
                >
                  <ClipboardList className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] text-[var(--ink)]">
                      {t("claimComms.create.form", "Form")}
                    </span>
                    <span className="whitespace-normal text-[11px] text-[var(--ink-3)]">
                      {t(
                        "claimComms.create.formHint",
                        "Ask anyone to fill in one of your forms",
                      )}
                    </span>
                  </span>
                </Button>
              ) : null}
            </PopoverContent>
          </Popover>
        ) : undefined
      }
    >
      {isPending ? (
        <div className="flex items-center gap-2 px-4 py-6 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t("claimComms.loading", "Loading communications...")}
        </div>
      ) : isError ? (
        <div className="flex items-center gap-3 px-4 py-6 text-[12px] text-[var(--ink-2)]">
          {t("claimComms.error", "Couldn't load communications.")}
          <Button variant="outline" size="xs" onClick={() => refetch()}>
            {t("common.retry", "Retry")}
          </Button>
        </div>
      ) : allRows.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">
          {t("claimComms.empty", "Nothing has been sent on this claim yet.")}
        </p>
      ) : (
        <>
          {/* Only when there is a split to make. A claim whose every message
              went one way needs no filter, and an always-on row of pills is
              chrome that earns nothing. */}
          {internalCount > 0 && externalCount > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pb-1 pt-3">
              <AudiencePill
                label={t("claimComms.filter.all", "All ({{count}})", {
                  count: allRows.length,
                })}
                active={audienceFilter === "all"}
                onClick={() => setAudienceFilter("all")}
              />
              <AudiencePill
                label={t("claimComms.filter.internal", "Internal ({{count}})", {
                  count: internalCount,
                })}
                active={audienceFilter === "internal"}
                onClick={() =>
                  setAudienceFilter(
                    audienceFilter === "internal" ? "all" : "internal",
                  )
                }
              />
              <AudiencePill
                label={t("claimComms.filter.external", "External ({{count}})", {
                  count: externalCount,
                })}
                active={audienceFilter === "external"}
                onClick={() =>
                  setAudienceFilter(
                    audienceFilter === "external" ? "all" : "external",
                  )
                }
              />
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto">
          {rows.map((row) => (
            <Button
              key={`${row.source}-${row.uuid}`}
              variant="ghost"
              onClick={() => setOpenRow(row)}
              className="flex h-auto w-full flex-col items-start gap-1 rounded-none border-b-[0.5px] border-b-[var(--rule)] px-4 py-3 text-left font-light last:border-b-0"
            >
              <span className="flex w-full items-center gap-2">
                <ActivityChannelPill channel={row.channel} />
                {/* The backend writes the subject into `summary`; it is the
                    only thing a reader scans for, so it takes the width. */}
                <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--ink)]">
                  {row.summary || t("claimComms.noSubject", "No subject")}
                </span>
                <ActivityStatusPill status={row.status} />
              </span>
              <span className="flex w-full items-center gap-2 text-[11px] text-[var(--ink-3)]">
                <AudienceBadge audience={row.audience} />
                <span className="min-w-0 flex-1 truncate">
                  {row.counterparty_name || row.counterparty}
                </span>
                <span className="shrink-0">
                  {formatDateTime(row.occurred_at)}
                </span>
              </span>
              {/* A message that never sent is the one a reader most needs
                  explaining, so the reason sits on the row, not behind a click. */}
              {row.skip_reason_label && (
                <ActivitySkipReason label={row.skip_reason_label} />
              )}
            </Button>
          ))}
          </div>
        </>
      )}

      <RecordForms recordType="claim" recordUuid={claimUuid} />
      {sendingForm ? (
        <SendFormDialog
          recordType="claim"
          recordUuid={claimUuid}
          onClose={() => setSendingForm(false)}
        />
      ) : null}

      <ClaimLetterDialog
        open={composeKind !== null}
        onOpenChange={(open) => !open && setComposeKind(null)}
        claimUuid={claimUuid}
        kind={composeKind ?? "letter"}
      />

      <Dialog
        open={!!openRow}
        onOpenChange={(open) => !open && setOpenRow(null)}
      >
        <DialogContent className="max-w-[1040px] sm:max-w-[1040px]">
          <DialogHeader>
            {/* The subject is what identifies a message to a reader, so it is
                the title. "Message" told them only which dialog they opened. */}
            <DialogTitle className="pr-8">
              {openRow?.summary || t("claimComms.detailTitle", "Message")}
            </DialogTitle>
            {openRow && (
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pt-1.5">
                <ActivityChannelPill channel={openRow.channel} />
                <ActivityStatusPill status={openRow.status} />
                <span className="text-[12px] text-[var(--ink-2)]">
                  {openRow.counterparty_name || openRow.counterparty}
                </span>
                <span className="text-[12px] text-[var(--ink-3)]">
                  {formatDateTime(openRow.occurred_at)}
                </span>
              </div>
            )}
          </DialogHeader>
          {openRow && <ActivityDetailPanel row={openRow} />}
        </DialogContent>
      </Dialog>
    </DetailCard>
  );
}

// ---------------------------------------------------------------------------

/**
 * Who could read this message. Mirrors the Notes card's own indicator - same
 * two words, same lock/eye pair - because a reader scanning a claim should not
 * have to learn the idea twice on one page.
 */
function AudienceBadge({ audience }: { audience: "internal" | "external" }) {
  const { t } = useTranslation();
  if (audience === "internal") {
    return (
      <span className="inline-flex shrink-0 items-center gap-[3px] text-[9px] font-normal tracking-[0.04em] text-[var(--ink-3)]">
        <Lock className="h-[9px] w-[9px]" />
        {t("claimComms.audience.internal", "Internal")}
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-[3px] text-[9px] font-normal tracking-[0.04em] text-[var(--green)]">
      <Eye className="h-[9px] w-[9px]" />
      {t("claimComms.audience.external", "External")}
    </span>
  );
}

function AudiencePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? "h-auto rounded-full border-[var(--green)] bg-[var(--green-bg)] px-2.5 py-0.5 text-[10px] font-normal text-[var(--ink)]"
          : "h-auto rounded-full border-[var(--rule)] bg-[var(--bg)] px-2.5 py-0.5 text-[10px] font-normal text-[var(--ink-3)]"
      }
    >
      {label}
    </Button>
  );
}
